import moment from 'moment';

import { InvoiceStatus } from 'lib/enums';
import {
  EOrganization,
  ESnapshot,
  ESnapshotExists,
  EUser,
  exists
} from 'lib/types';
import {
  SearchableInvoiceRecordFilter,
  SearchableInvoiceRecordSimpleFilter
} from 'lib/types/searchable';
import { isPublisherOrganization } from 'lib/utils/organizations';

export const ALL_INVOICES_STATUS = 'all';
export const PAID_INVOICES_STATUS = 'paid';
export const UNPAID_INVOICES_STATUS = 'unpaid';
export const REFUND_INVOICES_STATUS = 'refund';
export const VOIDED_INVOICES_STATUS = 'voided';
export const PAST_DUE_INVOICES_STATUS = 'past-due';
export const UNCOLLECTIBLE_INVOICES_STATUS = 'uncollectible';

export const INVOICE_STATUSE_OPTIONS = [
  { label: 'All', value: ALL_INVOICES_STATUS },
  { label: 'Past Due', value: PAST_DUE_INVOICES_STATUS },
  { label: 'Unpaid', value: UNPAID_INVOICES_STATUS },
  { label: 'Paid', value: PAID_INVOICES_STATUS },
  { label: 'Voided', value: VOIDED_INVOICES_STATUS },
  { label: 'Refund', value: REFUND_INVOICES_STATUS },
  { label: 'Uncollectible', value: UNCOLLECTIBLE_INVOICES_STATUS }
];

export const ALL_INVOICE_CREATION_DATES = 'all-time';
export const THIS_MONTH_INVOICE_CREATION_DATES = 'this-month';
export const LAST_MONTH_INVOICE_CREATION_DATES = 'last-month';
export const CUSTOM_INVOICE_CREATION_DATES = 'custom-date';

export type CreationDatesOptionValue =
  | typeof ALL_INVOICE_CREATION_DATES
  | typeof THIS_MONTH_INVOICE_CREATION_DATES
  | typeof LAST_MONTH_INVOICE_CREATION_DATES;

export const INVOICE_CREATION_DATE_OPTIONS = [
  { label: 'All time', value: ALL_INVOICE_CREATION_DATES },
  {
    label: `This month - ${moment()
      .startOf('month')
      .format('MMMM D')} to ${moment().format('MMMM D')}`,
    value: THIS_MONTH_INVOICE_CREATION_DATES
  },
  {
    label: `Last month - ${moment().subtract(1, 'month').format('MMMM')}`,
    value: LAST_MONTH_INVOICE_CREATION_DATES
  },
  { label: 'Custom date', value: CUSTOM_INVOICE_CREATION_DATES }
];
export type InvoiceTableStatus =
  | typeof ALL_INVOICES_STATUS
  | typeof PAID_INVOICES_STATUS
  | typeof UNPAID_INVOICES_STATUS
  | typeof REFUND_INVOICES_STATUS
  | typeof VOIDED_INVOICES_STATUS
  | typeof PAST_DUE_INVOICES_STATUS
  | typeof UNCOLLECTIBLE_INVOICES_STATUS;

export type InvoiceTableFilter = {
  // filter to only include invoices with any of the selected statuses
  invoiceTableStatuses: InvoiceTableStatus[];

  // filter based on run dates within a particular range
  dateRange:
    | {
        start: Date;
        end: Date;
      }
    | typeof ALL_INVOICE_CREATION_DATES
    | typeof THIS_MONTH_INVOICE_CREATION_DATES
    | typeof LAST_MONTH_INVOICE_CREATION_DATES;
};
export const DEFAULT_INVOICE_STATUS_FILTER: InvoiceTableFilter = {
  invoiceTableStatuses: [ALL_INVOICES_STATUS],
  dateRange: ALL_INVOICE_CREATION_DATES
};

/**
 * This function constructs the Elastic filters given the table filter and the user configuration
 */
export const getElasticFilters = (
  user: ESnapshotExists<EUser>,
  organization: ESnapshot<EOrganization> | null,
  invoiceFilter: InvoiceTableFilter
): {
  allFilters: SearchableInvoiceRecordFilter[];
  anyFilters: SearchableInvoiceRecordFilter[];
} => {
  // filter to only include invoices that have a defined invoicenumber field
  const allFilters: SearchableInvoiceRecordFilter[] = [
    { invoiceoutsidecolumn: [Number(false)] }
  ];
  const anyFilters: SearchableInvoiceRecordFilter[] = [];

  // Add filters to fetch invoices related to the current user/active org
  if (exists(organization)) {
    if (isPublisherOrganization(organization)) {
      allFilters.push({ publisherid: [organization.id] });
    } else {
      allFilters.push({ filerorganizationid: [organization.id] });
    }
  } else {
    allFilters.push({ filerid: [user.data().shadowUser || user.id] });
  }

  const effectiveStatuses = invoiceFilter.invoiceTableStatuses.filter(
    s => s !== ALL_INVOICES_STATUS
  );

  if (effectiveStatuses.length === 1) {
    // Single status: AND its conditions directly into allFilters
    allFilters.push(...getStatusConditions(effectiveStatuses[0]));
  } else if (effectiveStatuses.length > 1) {
    // Multiple statuses: OR each status' conditions together in anyFilters
    for (const status of effectiveStatuses) {
      anyFilters.push({ all: getStatusConditions(status) });
    }
  }

  // Add filters related to invoice creation date
  if (invoiceFilter.dateRange === THIS_MONTH_INVOICE_CREATION_DATES) {
    allFilters.push({
      created: { from: moment().utc().startOf('month').valueOf() }
    });
  } else if (invoiceFilter.dateRange === LAST_MONTH_INVOICE_CREATION_DATES) {
    allFilters.push({
      created: {
        from: moment().utc().subtract(1, 'month').startOf('month').valueOf(),
        to: moment().utc().startOf('month').valueOf()
      }
    });
  } else if (typeof invoiceFilter.dateRange !== 'string') {
    allFilters.push({
      created: {
        from: moment
          .utc(invoiceFilter.dateRange.start)
          .startOf('day')
          .valueOf(),
        to: moment.utc(invoiceFilter.dateRange.end).endOf('day').valueOf()
      }
    });
  }

  return { allFilters, anyFilters };
};

/**
 * Single source of truth for the Elastic conditions that define each invoice
 * status. The conditions are ANDed together: used directly in `allFilters` for a
 * single status, or wrapped in an `{ all }` group within `anyFilters` to OR
 * multiple statuses together.
 */
const getStatusConditions = (
  status: InvoiceTableStatus
): SearchableInvoiceRecordSimpleFilter[] => {
  switch (status) {
    // Voided invoices retain their prior status but display as "Void", so every
    // non-voided bucket excludes them; only VOIDED_INVOICES_STATUS surfaces them.
    case PAID_INVOICES_STATUS:
      return [
        { status: [InvoiceStatus.paid.value] },
        { voided: [Number(false)] }
      ];
    case REFUND_INVOICES_STATUS:
      return [
        {
          status: [
            InvoiceStatus.refunded.value,
            InvoiceStatus.partially_refunded.value
          ]
        },
        { voided: [Number(false)] }
      ];
    // Unpaid excludes past due: only invoices whose due date is still in the future
    case UNPAID_INVOICES_STATUS:
      return [
        { status: [InvoiceStatus.unpaid.value] },
        { voided: [Number(false)] },
        { duedate: { from: moment().utc().valueOf() } }
      ];
    case VOIDED_INVOICES_STATUS:
      return [{ voided: [Number(true)] }];
    case UNCOLLECTIBLE_INVOICES_STATUS:
      return [
        { status: [InvoiceStatus.uncollectible.value] },
        { voided: [Number(false)] }
      ];
    // Past due excludes voided invoices: unpaid, not voided, due date in the past
    case PAST_DUE_INVOICES_STATUS:
      return [
        { status: [InvoiceStatus.unpaid.value] },
        { voided: [Number(false)] },
        { duedate: { to: moment().utc().valueOf() } }
      ];
    default:
      return [];
  }
};

export const getInvoiceStatus = (
  status: number,
  due_date: number,
  voided_at: number,
  amount: number | undefined
) => {
  // in case of invoice has no line items or amount is 0
  if (!amount) {
    return InvoiceStatus.paid.label;
  }

  if (voided_at) {
    return 'Void';
  }

  if (
    ![
      InvoiceStatus.paid.value,
      InvoiceStatus.partially_refunded.value,
      InvoiceStatus.refunded.value,
      InvoiceStatus.uncollectible.value
    ].includes(status) &&
    moment.utc().unix() > due_date
  )
    return 'Past Due';

  return InvoiceStatus.by_value(status)?.label;
};
